import React, { useMemo, useState, useRef, useEffect } from 'react';
import { getProductReviews } from '../data/reviewsData';
import { fetchProductReviews } from '../services/medusa/reviews';
import './ProductReviews.css';
import { toSitePath } from 'utils/sitePath';

const PAGE_SIZE = 6;

const EMPTY_SUMMARY = { count: 0, average: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };

// Si el catálogo histórico solo permitía reseñar a quien compró, mostramos
// "Comprador verificado" en todas. Ver módulo product-review en el backend.
const TREAT_ALL_AS_VERIFIED = true;

// Fallback final para la foto cuadrada del lado derecho: solo se usa si el
// producto no tiene ninguna imagen (evita dejar la zona vacía).
const RIGHT_IMAGE_FALLBACK = 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/Fotos%20Mailing/Imagen_de_bienvenida_physiology.webp';

// Imagen de la sección de reseñas, gestionada por producto desde el admin de
// Medusa (sin código). Prioridad:
//   1) metadata.reviews_image  -> URL explícita si se quiere forzar una foto.
//   2) última imagen de la galería -> la que arrastres al final en el editor.
//   3) thumbnail del producto.
//   4) fallback de marca (RIGHT_IMAGE_FALLBACK).
function reviewImage(product) {
  const forced = toSitePath(product?.metadata?.reviews_image);
  if (forced && String(forced).trim()) return forced;
  const imgs = product?.images || [];
  if (imgs.length) return imgs[imgs.length - 1]?.url || imgs[imgs.length - 1];
  if (product?.thumbnail) return product.thumbnail;
  return RIGHT_IMAGE_FALLBACK;
}

// Etiquetas ES de los atributos tipo Typology (se muestran solo si el dato existe).
const ATTR_LABELS = {
  sebum: 'Nivel de sebo',
  age: 'Rango de edad',
  sensitive: 'Piel sensible',
  skinType: 'Tipo de piel',
};

function isVerified(r) {
  return TREAT_ALL_AS_VERIFIED || !!r.verified;
}

// Deriva {title, body} para reseñas heredadas sin título propio.
function splitTitleBody(r) {
  if (r.title) return { title: r.title, body: r.body || '' };
  const body = (r.body || '').trim();
  if (!body) return { title: '', body: '' };
  const m = body.match(/^(.+?[.!?])(\s+|$)/);
  if (m && m[1].length <= 70) {
    return { title: m[1].replace(/[.!?]+$/, ''), body: body.slice(m[0].length).trim() };
  }
  const words = body.split(/\s+/);
  if (words.length <= 8) return { title: body, body: '' };
  return { title: words.slice(0, 8).join(' ') + '…', body: words.slice(8).join(' ') };
}

function Stars({ value = 0, size = 14 }) {
  return (
    <span className="pr-stars" aria-label={`${value} de 5 estrellas`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.max(0, Math.min(1, value - (i - 1)));
        const id = `pr-g-${i}-${Math.round(fill * 100)}`;
        return (
          <svg key={i} viewBox="0 0 20 20" width={size} height={size} aria-hidden="true">
            <defs>
              <linearGradient id={id}>
                <stop offset={`${fill * 100}%`} stopColor="currentColor" />
                <stop offset={`${fill * 100}%`} stopColor="transparent" />
              </linearGradient>
            </defs>
            <path
              d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.49L10 14.98 5.06 16.7l.94-5.49-4-3.9 5.53-.8z"
              fill={`url(#${id})`}
              stroke="currentColor"
              strokeWidth="0.9"
              strokeLinejoin="round"
            />
          </svg>
        );
      })}
    </span>
  );
}

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
}

// Lista compacta de páginas: [1,2,3,4,5,'…',N]
function pageList(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (cur <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (cur >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', cur - 1, cur, cur + 1, '…', total];
}

export default function ProductReviews({ product }) {
  const topRef = useRef(null);

  // Reseñas desde la API de Medusa; fallback al export local si el backend no responde.
  const [data, setData] = useState({ reviews: [], summary: EMPTY_SUMMARY, loading: true });
  useEffect(() => {
    let alive = true;
    setData((d) => ({ ...d, loading: true }));
    fetchProductReviews(product?.id, { limit: 500 })
      .then((res) => { if (alive) setData({ reviews: res.reviews || [], summary: res.summary || EMPTY_SUMMARY, loading: false }); })
      .catch(() => { if (alive) { const local = getProductReviews(product); setData({ reviews: local.reviews, summary: local.summary, loading: false }); } });
    return () => { alive = false; };
  }, [product]);
  const { reviews, summary } = data;

  const [attrFilters, setAttrFilters] = useState({}); // { sebum: 'Seca', ... }
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});

  // Atributos presentes en el set → dropdowns de segmentación (tipo Typology).
  const attrOptions = useMemo(() => {
    const opts = {};
    for (const r of reviews) {
      for (const key of Object.keys(ATTR_LABELS)) {
        if (r[key]) {
          opts[key] = opts[key] || new Set();
          opts[key].add(r[key]);
        }
      }
    }
    return Object.fromEntries(Object.entries(opts).map(([k, v]) => [k, [...v]]));
  }, [reviews]);

  const filtered = useMemo(() => {
    let list = reviews;
    for (const [key, val] of Object.entries(attrFilters)) {
      if (val) list = list.filter((r) => r[key] === val);
    }
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1)); // más recientes
  }, [reviews, attrFilters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const { count, average } = summary;

  const goTo = (p) => {
    setPage(p);
    if (topRef.current) topRef.current.scrollIntoView({ block: 'start' });
  };
  const resetPage = () => setPage(1);

  // Sin reseñas (o aún cargando) no se pinta el bloque: evita una sección vacía.
  if (data.loading || !count) return null;

  const verifiedNote = TREAT_ALL_AS_VERIFIED ? ' de compras verificadas' : '';

  return (
    <section className="pr" ref={topRef}>
      <div className="pr-inner">
        <div className="pr-split">

          {/* Mitad izquierda: reseñas */}
          <div className="pr-left">
            <div className="pr-header">
              <span className="pr-header-score">{average.toFixed(1)}/5</span>
              <Stars value={average} size={18} />
              <span className="pr-header-text">
                Producto calificado {average.toFixed(1)}/5, basado en {count} {count === 1 ? 'reseña' : 'reseñas'}{verifiedNote}.
              </span>
            </div>

            {/* Filtros de segmentación (aparecen solo cuando las reseñas traen atributos) */}
            {Object.keys(attrOptions).length > 0 && (
              <div className="pr-controls">
                <div className="pr-filters">
                  {Object.entries(attrOptions).map(([key, values]) => (
                    <select
                      key={key}
                      className="pr-attr-select"
                      value={attrFilters[key] || ''}
                      onChange={(e) => { setAttrFilters((f) => ({ ...f, [key]: e.target.value })); resetPage(); }}
                    >
                      <option value="">{ATTR_LABELS[key]}</option>
                      {values.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  ))}
                </div>
              </div>
            )}

            <ul className="pr-list">
              {pageItems.map((r) => {
                const { title, body } = splitTitleBody(r);
                const isLong = body.length > 260;
                const open = expanded[r.id];
                return (
                  <li key={r.id} className="pr-item">
                    <div className="pr-item-side">
                      <span className="pr-author">{r.author || 'Cliente'}</span>
                      {isVerified(r) && <span className="pr-verified">Comprador verificado</span>}
                      <span className="pr-date">{formatShortDate(r.date)}</span>
                      {Object.keys(ATTR_LABELS).some((k) => r[k]) && (
                        <ul className="pr-attrs">
                          {Object.keys(ATTR_LABELS).map((k) => r[k] && (
                            <li key={k}><span className="pr-attr-k">{ATTR_LABELS[k]}:</span> {r[k]}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="pr-item-main">
                      <Stars value={r.rating} size={13} />
                      {title && <h3 className="pr-title">{title}</h3>}
                      {body && <p className={`pr-body${isLong && !open ? ' is-clamped' : ''}`}>{body}</p>}
                      {isLong && (
                        <button className="pr-showmore" onClick={() => setExpanded((e) => ({ ...e, [r.id]: !open }))}>
                          {open ? 'Ver menos' : 'Ver más'}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {pageItems.length === 0 && <p className="pr-empty">No hay reseñas con este filtro.</p>}

            {totalPages > 1 && (
              <nav className="pr-pager" aria-label="Paginación de reseñas">
                {pageList(safePage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`e${i}`} className="pr-pager-gap">…</span>
                  ) : (
                    <button
                      key={p}
                      className={`pr-pager-num${p === safePage ? ' is-active' : ''}`}
                      onClick={() => goTo(p)}
                      aria-current={p === safePage ? 'page' : undefined}
                    >
                      {String(p).padStart(2, '0')}
                    </button>
                  )
                )}
              </nav>
            )}
          </div>

          {/* Mitad derecha: foto cuadrada, tomada de la galería del producto
              (gestionable desde el admin, ver reviewImage). */}
          <div className="pr-right">
            <div className="pr-square">
              <img src={reviewImage(product)} alt={product?.title || 'Physiology'} />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
